import { QueryClient, UseQueryOptions, useQuery } from '@tanstack/react-query';
import produce from 'immer';

import { getQuizzes } from '../../api/quiz';
import { QuizItemCache } from '../../api/quiz/getQuizzes/normalize/toQuizItemCache';
import { uniqueIdFactory } from '../../api/quiz/getQuizzes/normalize/uniqueIds';
import { Folder, Quiz } from '../../api/quiz/getQuizzes/types';

function getAllQuizzes() {
  return getQuizzes();
}

// Takes up to two arrays of quizIds and folderIds and returns a consolidated
// array of uniqueIds representing all the quizzes and folders passed in.
function consolidateQuizzesFolders(
  quizIds: number[] = [],
  folderIds: number[] = []
) {
  return [
    ...quizIds.map(uniqueIdFactory.quiz.toUniqueId),
    ...folderIds.map(uniqueIdFactory.folder.toUniqueId),
  ];
}

export const quizzesQueryKey = {
  quiz: ['quiz'] as const,
};

const QUIZZES_STALE_TIME = 1200000; // 20 minutes

type UseQuizzesOptions<T> = Partial<
  Pick<
    UseQueryOptions<QuizItemCache, unknown, T, readonly ['quiz']>,
    'enabled' | 'select'
  >
>;

export function useQuizzes<T = QuizItemCache>({
  select,
  enabled = true,
}: UseQuizzesOptions<T> = {}) {
  return useQuery({
    queryKey: quizzesQueryKey.quiz,
    queryFn: getAllQuizzes,
    staleTime: QUIZZES_STALE_TIME,
    select: select,
    enabled: enabled,
  });
}

export function deleteQuizOrFolderFromCache(
  queryClient: QueryClient,
  uniqueId: string
) {
  queryClient.setQueryData<QuizItemCache>(
    quizzesQueryKey.quiz,
    (quizItemCache) => {
      return produce(quizItemCache, (newQuizItemCache) => {
        newQuizItemCache.itemById[uniqueId]['deleted'] = true;

        if (uniqueId in newQuizItemCache.rootItems) {
          newQuizItemCache.rootItems = newQuizItemCache.rootItems.filter(
            (uniqueId) => uniqueId !== uniqueId
          );
        } else {
          let key: keyof typeof newQuizItemCache.itemById;
          for (key in newQuizItemCache.itemById) {
            if (
              'children' in newQuizItemCache.itemById[key] &&
              newQuizItemCache.itemById[key]['children'].includes(uniqueId)
            ) {
              newQuizItemCache.itemById[key]['children'] =
                newQuizItemCache.itemById[key]['children'].filter(
                  (uniqueId) => uniqueId !== uniqueId
                );
            }
          }
        }
      });
    }
  );
}
const quizItemUpdater = ({
  quizId,
  updatedQuiz,
}: {
  quizId: number;
  updatedQuiz: Quiz;
}) =>
  produce((quizData: QuizItemCache) => {
    const { itemById } = quizData;

    itemById[`quiz-${quizId}`] = updatedQuiz;
  });

export function updateQuizItemCacheById(
  queryClient: QueryClient,
  { updatedQuiz, quizId }: { updatedQuiz: Quiz; quizId: number }
) {
  queryClient.setQueryData<QuizItemCache>(
    quizzesQueryKey.quiz,
    quizItemUpdater({ quizId, updatedQuiz })
  );
}

export function addQuizToCache(queryClient: QueryClient, quiz: Quiz) {
  queryClient.setQueryData<QuizItemCache>(
    quizzesQueryKey.quiz,
    (quizItemCache) => {
      return produce(quizItemCache, (newQuizItemCache) => {
        newQuizItemCache.itemById[quiz.uniqueId] = {
          id: quiz.id,
          name: quiz.name,
          parentId: quiz.parentId,
          type: 'quiz',
          uniqueId: quiz.uniqueId,
          date: new Date(quiz.date),
          soc: quiz.soc,
          deleted: false,
          version: quiz.version,
          sharable: quiz.sharable,
          standard: null,
          locked: quiz.locked,
        };

        newQuizItemCache.itemIdByType.quiz.push(quiz.uniqueId);

        if (quiz.parentId === null) {
          newQuizItemCache.rootItems.push(quiz.uniqueId);
        } else {
          const uniqueParentId = uniqueIdFactory.folder.toUniqueId(
            quiz.parentId
          );
          const parent = newQuizItemCache.itemById[uniqueParentId] as Folder;
          parent.children.push(quiz.uniqueId);
        }
      });
    }
  );
}

// Optimized for bulk deletion of quizzes and/or folders
export function bulkDeleteQuizzesFoldersFromCache(
  queryClient: QueryClient,
  quizzes?: number[],
  folders?: number[]
) {
  queryClient.setQueryData<QuizItemCache>(
    quizzesQueryKey.quiz,
    (quizItemCache) => {
      return produce(quizItemCache, (newQuizItemCache) => {
        let itemsToDelete: string[] = [];
        itemsToDelete = consolidateQuizzesFolders(quizzes, folders);

        // Delete in itemById property of cache
        for (const uniqueId of itemsToDelete) {
          newQuizItemCache.itemById[uniqueId]['deleted'] = true;
        }

        // Delete from rootItems property of cache
        newQuizItemCache.rootItems = newQuizItemCache.rootItems.filter(
          (uniqueId) => !itemsToDelete.includes(uniqueId)
        );

        // Delete from children arrays of all items in cache
        let key: keyof typeof newQuizItemCache.itemById;
        for (key in newQuizItemCache.itemById) {
          if (newQuizItemCache.itemById[key]['children']) {
            newQuizItemCache.itemById[key]['children'] =
              newQuizItemCache.itemById[key]['children'].filter(
                (uniqueId) => !itemsToDelete.includes(uniqueId)
              );
          }
        }
      });
    }
  );
}

export function purgeQuizzesFoldersFromCache(
  queryClient: QueryClient,
  quizzes?: number[],
  folders?: number[]
) {
  queryClient.setQueryData<QuizItemCache>(
    quizzesQueryKey.quiz,
    (quizItemCache) => {
      return produce(quizItemCache, (newQuizItemCache) => {
        const itemsToPurge = consolidateQuizzesFolders(quizzes, folders);
        for (const itemId in itemsToPurge) {
          delete newQuizItemCache.itemById[itemId];
        }
        newQuizItemCache.rootItems = newQuizItemCache.rootItems.filter(
          (itemId) => !itemsToPurge.includes(itemId)
        );
        // Delete from children arrays of all items in cache
        let key: keyof typeof newQuizItemCache.itemById;
        for (key in newQuizItemCache.itemById) {
          if (newQuizItemCache.itemById[key]['children']) {
            newQuizItemCache.itemById[key]['children'] =
              newQuizItemCache.itemById[key]['children'].filter(
                (uniqueId) => !itemsToPurge.includes(uniqueId)
              );
          }
        }
      });
    }
  );
}

export function restoreQuizzesFoldersInCache(
  queryClient: QueryClient,
  quizzes?: number[],
  folders?: number[]
) {
  queryClient.setQueryData<QuizItemCache>(
    quizzesQueryKey.quiz,
    (quizItemCache) => {
      return produce(quizItemCache, (newQuizItemCache) => {
        const itemsToRestore = consolidateQuizzesFolders(quizzes, folders);
        for (let i = 0; i < itemsToRestore.length; i++) {
          newQuizItemCache.itemById[itemsToRestore[i]].deleted = false;
        }
      });
    }
  );
}
